import React, { useEffect, useRef, useState } from "react";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { getTemplateVersions } from "@/utils/Services";
import { ITemplateVersion } from "@/utils/Types";
import { GitBranch, CheckCircle, ListDashes } from "@phosphor-icons/react";
import { Loader2 } from "lucide-react";
import { useIsAdmin } from "@/hooks/useIsAdmin";
import { isEmpty } from "lodash";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import useIsMobile from "@/hooks/useIsMobile";
import useUnsavedChangesStore from "@/utils/UnsavedChangesStore";

interface TemplateVersionSelectorProps {
  templateId: string;
  currentVersion?: ITemplateVersion;
  onVersionSelect: (version: ITemplateVersion) => void;
  selectedVrsn?: ITemplateVersion;
  newVersion?: ITemplateVersion;
  toggleActive?: boolean;
  refetch?: boolean;
  onManageVersions?: () => void;
  resetRefetch?: () => void;
  resetActive?: () => void;
  setShowUnsavedDialog?: (show: boolean) => void;
  changeVersion?: boolean;
  resetChangeVersion?: () => void;
}

const TemplateVersionSelector: React.FC<TemplateVersionSelectorProps> = ({
  templateId,
  currentVersion,
  onVersionSelect,
  selectedVrsn,
  newVersion,
  toggleActive,
  refetch,
  onManageVersions,
  resetRefetch,
  resetActive,
  setShowUnsavedDialog,
  changeVersion,
  resetChangeVersion,
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [versions, setVersions] = useState<ITemplateVersion[]>([]);
  const [selectedVersion, setSelectedVersion] =
    useState<ITemplateVersion | null>(null);
  const [hasMore, setHasMore] = useState(true);
  const [offset, setOffset] = useState(0);
  const [limit] = useState(20);
  const [totalCount, setTotalCount] = useState(0);
  const isAdmin = useIsAdmin();

  const [pendingVersion, setPendingVersion] = useState<ITemplateVersion | null>(
    null
  );

  // const canUpdate = useRef(false);
  const isMobile = useIsMobile();
  const [open, setOpen] = useState(false);
  const {
    pendingNavigation,
    setPendingNavigation,
    unsavedChanges,
    setUnsavedChanges,
  } = useUnsavedChangesStore();

  const fetchVersions = async (off = 0, append = false) => {
    if (!templateId || templateId === "create") return;

    setIsLoading(true);
    try {
      const response = await getTemplateVersions(templateId, {
        limit: limit,
        offset: off,
        ...(isAdmin && { admin_dashboard: isAdmin }),
      });

      if (append) {
        setVersions((prev) => [...prev, ...response?.results]);
      } else {
        setVersions(response?.results);
      }

      if (response?.results?.length > 0 && !selectedVersion) {
        setSelectedVersion(response?.results[0]);
        // onVersionSelect(response?.results[0]);
      }

      setTotalCount(response?.count);
      setHasMore(response?.count > off + response?.results.length);
    } catch (error) {
      console.error("Error fetching template versions:", error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (selectedVrsn) {
      setSelectedVersion(selectedVrsn);
      if (selectedVrsn?.published) {
        setVersions((prev) =>
          prev.map((v) =>
            v.id == selectedVrsn?.id
              ? { ...selectedVrsn, published: true }
              : { ...v, published: false }
          )
        );
      }
    }
  }, [selectedVrsn]);

  const loadMoreVersions = () => {
    if (!hasMore || isLoading) return;
    const newOffset = offset + limit;
    setOffset(newOffset);
    fetchVersions(newOffset, true);
  };

  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    if (scrollHeight - scrollTop <= clientHeight * 1.5) {
      loadMoreVersions();
    }
  };

  useEffect(() => {
    if (templateId) {
      setVersions([]);
      setOffset(0);
      fetchVersions(0, false);
    }
  }, [templateId]);

  const displayVersionNumber =
    currentVersion?.version ||
    versions.find((v) => v.id === currentVersion?.id)?.version ||
    "";

  useEffect(() => {
    if (currentVersion && toggleActive) {
      setVersions((prev) =>
        prev.map((v) =>
          v.id === currentVersion?.id
            ? { ...currentVersion, published: true }
            : { ...v, published: false }
        )
      );
      setSelectedVersion({ ...currentVersion, published: true });
    }
    resetActive?.();
  }, [toggleActive, currentVersion]);

  useEffect(() => {
    if (!isEmpty(newVersion)) {
      setSelectedVersion(newVersion);
      setVersions((prev) => [
        { ...newVersion, published: newVersion.published || false },
        ...prev.map((v) => ({ ...v, published: false })),
      ]);
      const newOffset = offset + 1;
      setOffset(newOffset);
    }
  }, [newVersion]);

  useEffect(() => {
    if (refetch) {
      fetchVersions(0, false);
      if (selectedVersion) {
        setSelectedVersion({
          ...selectedVersion,
          published: false,
        });
      }
      resetRefetch?.();
    }
  }, [refetch]);

  useEffect(() => {
    if (changeVersion) {
      onVersionSelect(pendingVersion!);
      setSelectedVersion(pendingVersion!);
      resetChangeVersion?.();
    }
  }, [changeVersion]);

  return (
    <div className="flex items-center mr-3">
      <Select
        onValueChange={(value) => {
          const selectedVersion = versions.find((v) => v.id === value);
          if (selectedVersion) {
            if (unsavedChanges) {
              setPendingVersion(selectedVersion);
              setShowUnsavedDialog?.(true);
            } else {
              onVersionSelect(selectedVersion);
              setSelectedVersion(selectedVersion);
            }
          }
        }}
        disabled={isLoading}
        value={selectedVersion?.id}
        open={open}
        onOpenChange={setOpen}
      >
        <SelectTrigger className="min-w-[10px] h-[32px] border-gray-50 dark:border-gray-600 bg-background text-gray-950 dark:text-white">
          {versions.length > 0 ? (
            <div className="flex items-center gap-1">
              <GitBranch size={16} />
              <span>v{displayVersionNumber}</span>
              {selectedVersion?.published && !isMobile && (
                <div className="mx-1 flex items-center gap-1 text-xs text-green-500 dark:text-green-400">
                  <span>Active</span>
                </div>
              )}
            </div>
          ) : (
            <div className="flex items-center gap-1">
              <Loader2 size={16} />
              {!isMobile && (
                <span className="text-xs text-gray-500 dark:text-gray-100">
                  Loading...
                </span>
              )}
            </div>
          )}
        </SelectTrigger>
        <SelectContent
          showArrow={false}
          className="min-w-[150px] max-h-[300px] overflow-y-auto dark:bg-gray-800 dark:border-gray-700"
        >
          <div
            onScroll={handleScroll}
            className="max-h-[300px] overflow-y-auto"
          >
            <SelectGroup>
              {versions.map((version) => (
                <SelectItem
                  key={version.id}
                  value={version.id}
                  className="my-1 cursor-pointer py-2 px-3 hover:bg-gray-25/50 dark:hover:bg-gray-500 data-[state=checked]:bg-gray-25 dark:data-[state=checked]:bg-gray-950 [&_span.absolute]:hidden"
                >
                  <div className="flex items-center justify-between w-full">
                    <div className="flex flex-col">
                      <div className="flex items-center">
                        <span className="font-medium">
                          Version {version.version}
                        </span>
                      </div>
                      {version.published && (
                        <div className="flex items-center gap-1 text-xs text-green-500 dark:text-green-400">
                          <span>Active</span>
                        </div>
                      )}
                      <span className="text-xs text-gray-500 dark:text-gray-100">
                        {new Date(version.created_at).toLocaleDateString()}
                      </span>
                    </div>
                  </div>
                </SelectItem>
              ))}
              {isLoading && (
                <div className="flex items-center justify-center p-2">
                  <Loader2 className="h-4 w-4 animate-spin text-gray-500 dark:text-gray-400" />
                </div>
              )}
              {!isLoading && versions.length === 0 && (
                <div className="p-2 text-center text-gray-500 dark:text-gray-100">
                  No versions found
                </div>
              )}
            </SelectGroup>
          </div>

          <div className="sticky bottom-0 border-t border-gray-25 dark:border-gray-700 bg-white dark:bg-gray-800 p-1">
            <Button
              variant="ghost"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                if (onManageVersions) {
                  onManageVersions();
                  setOpen(false);
                }
              }}
              className="w-full flex items-center justify-center gap-2 py-2 px-1 text-sm font-medium rounded-md hover:bg-gray-25/50 dark:hover:bg-gray-500 text-gray-700 dark:text-white transition-colors"
            >
              <ListDashes size={16} />
              <span>Manage Versions</span>
            </Button>
          </div>
        </SelectContent>
      </Select>
    </div>
  );
};

export default TemplateVersionSelector;
